import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { journalEntrySchema, createJournalEntryRequestSchema, } from './journalEntry.api.js';
const journalResponseSchema = z.object({
    id: z.string(),
    title: z.string(),
    labels: z.array(z.string()),
    suggestedTitle: z.string().optional(),
    entries: z.array(journalEntrySchema),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
const journalSummaryResponseSchema = z.object({
    id: z.string(),
    title: z.string(),
    labels: z.array(z.string()),
    suggestedTitle: z.string().optional(),
    previewText: z.string().optional(),
    previewPhotoEntryId: z.string().optional(),
    photoCount: z.number().int().nonnegative(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
const journalListResponseSchema = z.object({
    journals: z.array(journalSummaryResponseSchema),
});
export const createJournalRequestSchema = z.object({
    title: z.string().min(1, 'Title is required'),
    labels: z.array(z.string()).optional(),
    entries: z.array(createJournalEntryRequestSchema).optional(),
});
const createJournalResponseSchema = z.object({
    // id: z.string(),
    // title: z.string(),
    // labels: z.array(z.string()),
    // createdAt: z.string(),
    journalId: z.string(),
});
export const updateJournalRequestSchema = z.object({
    title: z.string().min(1, 'Title cannot be empty').optional(),
    labels: z.array(z.string()).optional(),
});
export const useCreateJournal = (authenticatedRequest) => useMutation({
    mutationFn: (data) => {
        const validatedData = createJournalRequestSchema.parse(data);
        return authenticatedRequest({
            url: '/journals',
            method: 'POST',
            data: validatedData,
        }, createJournalResponseSchema);
    },
});
/**
 * How many journals each page of the journal list loads. Small so the first page is quick;
 * infinite scrolling loads further pages until the screen is full.
 */
export const JOURNAL_PAGE_SIZE = 8;
/**
 * How long the journal list counts as fresh, so returning to it from a journal does not refetch.
 * Every journal mutation invalidates ['journal'], which refetches regardless.
 */
const JOURNAL_LIST_STALE_TIME_MS = 60 * 1000;
/**
 * The user's journals a page at a time (newest first), for infinite scrolling. Call
 * `fetchNextPage` for the next page and join the pages with `journalPageItems`. The list API
 * returns no total, so a page that comes back full means there may be another.
 */
export const useGetJournals = (authenticatedRequest, autoFetch = true) => useInfiniteQuery({
    queryKey: ['journal'],
    initialPageParam: 1,
    enabled: autoFetch,
    staleTime: JOURNAL_LIST_STALE_TIME_MS,
    queryFn: async ({ pageParam }) => {
        const response = await authenticatedRequest({
            url: '/journals',
            method: 'GET',
            params: { page: pageParam, limit: JOURNAL_PAGE_SIZE },
        }, journalListResponseSchema);
        return { journals: (response?.data?.journals ?? []), page: pageParam };
    },
    getNextPageParam: (lastPage) => lastPage.journals.length >= JOURNAL_PAGE_SIZE ? lastPage.page + 1 : undefined,
});
/** Joins the loaded pages of `useGetJournals` into one list. */
export const journalPageItems = (data) => data?.pages.flatMap((page) => page.journals) ?? [];
export const useGetJournal = (authenticatedRequest, journalId) => {
    if (!journalId || journalId === 'new') {
        return { data: undefined, loading: false, error: null };
    }
    return useQuery({
        queryKey: ['journal', journalId],
        queryFn: ({ queryKey }) => {
            const [, journalId] = queryKey;
            const validatedJournalId = z.string().min(1).parse(journalId);
            return authenticatedRequest({
                url: `/journals/${validatedJournalId}`,
                method: 'GET',
            }, journalResponseSchema);
        },
    });
};
/**
 * Re-fetches one journal every `intervalMs` while `enabled`, for waiting on a
 * field the background tagging function fills in. Shares its cache entry with
 * `useGetJournal`.
 */
export const usePollJournal = (authenticatedRequest, journalId, enabled, intervalMs) => useQuery({
    queryKey: ['journal', journalId],
    queryFn: () => authenticatedRequest({
        url: `/journals/${z.string().min(1).parse(journalId)}`,
        method: 'GET',
    }, journalResponseSchema),
    enabled,
    refetchInterval: enabled ? intervalMs : false,
});
export const useUpdateJournal = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => {
            const validatedJournalId = z.string().min(1).parse(data.journalId);
            const validatedData = updateJournalRequestSchema.parse({
                title: data.title,
                labels: data.labels,
            });
            return authenticatedRequest({
                url: `/journals/${validatedJournalId}`,
                method: 'PUT',
                data: validatedData,
            });
        },
        onSuccess: (_response, variables) => {
            queryClient.invalidateQueries({ queryKey: ['journal', variables.journalId] });
            queryClient.invalidateQueries({ queryKey: ['journal'] });
        },
    });
};
export const useDeleteJournal = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (journalId) => {
            const validatedJournalId = z.string().min(1).parse(journalId);
            return authenticatedRequest({
                url: `/journals/${validatedJournalId}`,
                method: 'DELETE',
            });
        },
        onSuccess: () => {
            // The list is cached (staleTime), so it must be told the journal is gone. exact, so the
            // still-mounted detail query does not refetch the deleted journal.
            queryClient.invalidateQueries({ queryKey: ['journal'], exact: true });
        },
    });
};
